import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";

const zipGeoURL = "/geo/tl_2020_us_zcta520.geojson";

// Share one parsed copy of the ~26MB national ZCTA file per session.
let cachedZipGeoPromise = null;
const loadZipGeo = () => {
  if (!cachedZipGeoPromise) {
    cachedZipGeoPromise = fetch(zipGeoURL).then((res) => {
      if (!res.ok) {
        cachedZipGeoPromise = null;
        throw new Error(`geo fetch failed: ${res.status}`);
      }
      return res.json();
    });
  }
  return cachedZipGeoPromise;
};

const extractCoords = (coords) => {
  if (!Array.isArray(coords)) return [];
  if (coords.length >= 2 && typeof coords[0] === "number" && typeof coords[1] === "number") {
    return [coords];
  }
  return coords.flatMap(extractCoords);
};

function FitOnce({ features }) {
  const map = useMap();
  if (process.env.NODE_ENV === "development") window.__practiceMap = map;
  const fittedKeyRef = React.useRef("");
  // Refit whenever the SET of shown areas changes, and only after the map has
  // its real size (a fit computed mid-layout picks a far-too-wide zoom).
  const key = features
    .map((f) => String(f.properties?.ZCTA5CE20))
    .sort()
    .join(",");
  useEffect(() => {
    if (!features.length || fittedKeyRef.current === key) return;
    const timer = setTimeout(() => {
      map.invalidateSize();
      const pts = features.flatMap((f) => extractCoords(f.geometry?.coordinates || []));
      if (!pts.length) return;
      const lats = pts.map((p) => p[1]);
      const lngs = pts.map((p) => p[0]);
      map.fitBounds(
        [
          [Math.min(...lats), Math.min(...lngs)],
          [Math.max(...lats), Math.max(...lngs)],
        ],
        { padding: [20, 20], maxZoom: 11 }
      );
      fittedKeyRef.current = key;
    }, 250);
    return () => clearTimeout(timer);
  }, [key, features, map]);
  return null;
}

/**
 * Practice heat map: where logged patients come from (green fill, scaled by
 * count) and where my referrers practice (blue outline). Zero-PHI — only
 * ZIP-level tallies ever reach the browser.
 */
export default function PracticeMap({ patientZips, providerZips }) {
  // patientZips: {zip: count}; providerZips: {zip: [names]}
  const [mode, setMode] = useState("both"); // patients | providers | both
  const [geo, setGeo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    loadZipGeo()
      .then((g) => alive && setGeo(g))
      .catch((e) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, []);

  const showPatients = mode !== "providers";
  const showProviders = mode !== "patients";

  const wanted = useMemo(() => {
    const set = new Set();
    if (showPatients) Object.keys(patientZips || {}).forEach((z) => set.add(z));
    if (showProviders) Object.keys(providerZips || {}).forEach((z) => set.add(z));
    return set;
  }, [patientZips, providerZips, showPatients, showProviders]);

  const features = useMemo(() => {
    if (!geo || wanted.size === 0) return [];
    return geo.features.filter((f) => wanted.has(String(f.properties.ZCTA5CE20)));
  }, [geo, wanted]);

  const maxPatients = useMemo(
    () => Math.max(1, ...Object.values(patientZips || {})),
    [patientZips]
  );
  const maxProviders = useMemo(
    () => Math.max(1, ...Object.values(providerZips || {}).map((v) => (Array.isArray(v) ? v.length : Number(v) || 0))),
    [providerZips]
  );

  // True choropleth ramps — a ZIP with 12 patients must read clearly darker
  // than one with 2, so counts map to discrete color steps, not opacity.
  const GREEN_RAMP = ["#d9e9dd", "#a9ceb3", "#76ae85", "#4b8a5d", "#2c5e3c"];
  const BLUE_RAMP = ["#d7e6f8", "#a5c8ef", "#70a6e3", "#3f82d3", "#1f5cab"];
  const rampColor = (ramp, count, max) => {
    if (count <= 0) return null;
    // sqrt spreads the low end so small practices still see variation
    const t = Math.sqrt(count / max);
    return ramp[Math.min(ramp.length - 1, Math.floor(t * ramp.length))];
  };
  const providerCount = (zip) => {
    const v = providerZips?.[zip];
    return Array.isArray(v) ? v.length : Number(v) || 0;
  };

  const style = (feature) => {
    const zip = String(feature.properties.ZCTA5CE20);
    const pts = showPatients ? patientZips?.[zip] || 0 : 0;
    const provs = showProviders ? providerCount(zip) : 0;

    if (pts > 0) {
      // Patients win the fill; referrer presence shows as the blue outline,
      // darker/thicker with more referrers there.
      return {
        fillColor: rampColor(GREEN_RAMP, pts, maxPatients),
        fillOpacity: 0.75,
        color: provs > 0 ? rampColor(BLUE_RAMP, provs, maxProviders) : "#2c5e3c",
        weight: provs > 0 ? 2.5 : 1,
        opacity: 0.9,
      };
    }
    if (provs > 0) {
      // Referrer-only ZIP: blue fill scaled by how many referrers practice there.
      return {
        fillColor: rampColor(BLUE_RAMP, provs, maxProviders),
        fillOpacity: mode === "providers" ? 0.75 : 0.45,
        color: "#1f5cab",
        weight: 2,
        opacity: 0.9,
      };
    }
    return { fillOpacity: 0, opacity: 0, weight: 0 };
  };

  const onEachFeature = (feature, layer) => {
    const zip = String(feature.properties.ZCTA5CE20);
    const pts = patientZips?.[zip] || 0;
    const provs = providerZips?.[zip] || [];
    const parts = [`<b>ZIP ${zip}</b>`];
    if (pts) parts.push(`${pts} patient${pts !== 1 ? "s" : ""} from here`);
    if (provs.length) parts.push(`${provs.length} referrer${provs.length !== 1 ? "s" : ""}: ${provs.slice(0, 4).join(", ")}${provs.length > 4 ? "…" : ""}`);
    layer.bindTooltip(parts.join("<br>"));
  };

  const empty = !Object.keys(patientZips || {}).length && !Object.keys(providerZips || {}).length;

  return (
    <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="font-semibold text-sm">Practice Map</h3>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1" title="Darker green = more patients from that ZIP">
            <span className="flex">
              {["#d9e9dd", "#76ae85", "#2c5e3c"].map((c) => (
                <span key={c} className="inline-block w-2.5 h-3" style={{ background: c }} />
              ))}
            </span>
            Patients
          </span>
          <span className="flex items-center gap-1" title="Darker blue = more of my referrers practice there">
            <span className="flex">
              {["#d7e6f8", "#70a6e3", "#1f5cab"].map((c) => (
                <span key={c} className="inline-block w-2.5 h-3" style={{ background: c }} />
              ))}
            </span>
            My referrers
          </span>
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="border rounded px-1.5 py-1">
            <option value="both">Patients + Providers</option>
            <option value="patients">Patients only</option>
            <option value="providers">Providers only</option>
          </select>
        </div>
      </div>
      {empty ? (
        <div className="p-6 text-center text-sm text-gray-500 bg-gray-50 rounded">
          Log referrals with a patient ZIP (and add referrers) to light up the map.
        </div>
      ) : error ? (
        <div className="p-4 text-sm text-red-600">Map data unavailable: {error}</div>
      ) : !geo ? (
        <div className="p-6 text-center text-sm text-gray-500">Loading map…</div>
      ) : (
        <div className="h-80 rounded overflow-hidden border">
          <MapContainer center={[39.5, -86.1]} zoom={6} style={{ width: "100%", height: "100%" }} scrollWheelZoom={true} preferCanvas={true}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
            <GeoJSON key={`${mode}-${features.length}`} data={{ type: "FeatureCollection", features }} style={style} onEachFeature={onEachFeature} />
            <FitOnce features={features} />
          </MapContainer>
        </div>
      )}
      {Object.keys(patientZips || {}).length > 0 && (
        <div className="mt-2">
          <div className="text-[11px] text-gray-400 mb-1">
            Patient ZIPs — click one for area analytics (referrers, payer split, penetrance):
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(patientZips)
              .sort((a, b) => b[1] - a[1])
              .map(([zip, count]) => (
                <Link
                  key={zip}
                  to={`/refer/analytics/zip/${zip}`}
                  className="px-2 py-1 bg-green-50 border border-green-200 rounded text-xs hover:bg-green-100 hover:border-green-400"
                  title={`Open analytics for ZIP ${zip}`}
                >
                  <span className="font-semibold text-primary">{zip}</span>
                  <span className="text-gray-600"> · {count} pt{count !== 1 ? "s" : ""}</span>
                </Link>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
